/**
 * Changelog data source.
 *
 * Release notes live here as structured data so they can feed the public
 * /changelog page, the RSS feed at /changelog/rss.xml, and JSON-LD from one
 * source of truth. Entries follow Keep a Changelog semantics with SemVer
 * versions; `date` is ISO-8601 and must be a real release date.
 *
 * Every user-visible string is bilingual. Release notes are product
 * documentation, not chrome — they are read by customers deciding whether to
 * upgrade, so they render from data in the active locale rather than relying
 * on the DOM-level AutoTranslate fallback.
 *
 * Adding a release: prepend an entry with the next version and today's date.
 * tests/unit/changelog.test.mts enforces ordering, shape and Thai coverage, so
 * an untranslated string fails `npm test` rather than shipping half-English.
 */

export type ChangeKind = 'added' | 'changed' | 'fixed' | 'removed' | 'security';

/** A user-visible string with an English source and a Thai translation. */
export interface LocalisedText {
  en: string;
  th: string;
}

export interface ChangelogEntry {
  version: string;
  date: string; // ISO date, e.g. '2026-10-02'
  title: LocalisedText;
  summary: LocalisedText;
  changes: { kind: ChangeKind; text: LocalisedText }[];
  /** True for releases that represent a breaking change for integrators. */
  breaking?: boolean;
}

export const changelog: ChangelogEntry[] = [
  {
    version: '1.7.0',
    date: '2026-10-02',
    title: {
      en: 'Reliability, releases and product lifecycle',
      th: 'ความน่าเชื่อถือ การออกรุ่น และวงจรชีวิตผลิตภัณฑ์',
    },
    summary: {
      en: 'A dated public changelog, a documented release process, feature flags for every risky surface, and an error pipeline that captures what users actually hit.',
      th: 'บันทึกการเปลี่ยนแปลงสาธารณะที่ระบุวันที่ กระบวนการออกรุ่นที่เป็นเอกสาร ธงฟีเจอร์สำหรับทุกส่วนที่เสี่ยง และระบบรายงานข้อผิดพลาดที่บันทึกปัญหาที่ผู้ใช้พบจริง',
    },
    changes: [
      {
        kind: 'added',
        text: {
          en: 'Public changelog with dated release notes, breaking-change flags and an RSS feed',
          th: 'บันทึกการเปลี่ยนแปลงสาธารณะพร้อมวันที่รุ่น การทำเครื่องหมายการเปลี่ยนที่พักภูมิ และฟีด RSS',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Feature-flag registry so any risky surface can be switched off without a code release',
          th: 'ทะเบียนธงฟีเจอร์เพื่อให้ปิดส่วนที่เสี่ยงได้ทันทีโดยไม่ต้องออกโค้ดใหม่',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Client error reporting with sanitized payloads, de-duplication and per-IP rate limiting',
          th: 'ระบบรายงานข้อผิดพลาดจากฝั่งผู้ใช้ พร้อมตัดข้อมูลอ่อนไหว ตัดรายการซ้ำ และจำกัดอัตราต่อ IP',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Loading skeletons for every authenticated module and retryable route-level error boundaries',
          th: 'หน้าโหลดสเกลเลโตนสำหรับทุกโมดูลที่ต้องเข้าสู่ระบบ และหน้าจัดการข้อผิดพลาดระดับหน้าที่ลองใหม่ได้',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Scheduled trial lifecycle jobs: expiry processing and trial-ending, expiry and win-back emails',
          th: 'งานอัตโนมัติวงจรชีวิตการทดลองใช้ ทั้งประมวลผลการหมดอายุ และอีเมลแจ้งใกล้หมดอายุ หมดอายุ และดึงลูกค้ากลับมาใช้บริการ',
        },
      },
      {
        kind: 'changed',
        text: {
          en: 'Trial and licence state now reconciles from server entitlements, with the browser cache only as an offline fallback',
          th: 'สถานะการทดลองใช้และใบอนุญาตคำนวณจากสิทธิ์การใช้งานบนเซิร์ฟเวอร์ โดยแคชในเบราว์เซอร์ใช้เป็นเพียงข้อมูลสำรองเมื่อออฟไลน์',
        },
      },
      {
        kind: 'changed',
        text: {
          en: 'Release notes published bilingually in Thai and English',
          th: 'เผยแพร่บันทึกการเปลี่ยนแปลงเป็นภาษาไทยและอังกฤษ',
        },
      },
      {
        kind: 'fixed',
        text: {
          en: 'Trial expiry could previously drift from server state when a device changed or the clock was wrong',
          th: 'การหมดอายุการทดลองใช้เคยคลาดเคลื่อนจากสถานะบนเซิร์ฟเวอร์ได้ เมื่อเปลี่ยนอุปกรณ์หรือนาฬิกาไม่ตรง',
        },
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-10-02',
    title: {
      en: 'Quality gates and accessibility conformance',
      th: 'เกณฑ์คุณภาพและการเข้าถึงตามมาตรฐาน',
    },
    summary: {
      en: 'Every release now passes an automated quality pipeline: lint, types, unit tests, translation coverage, and SEO/accessibility/performance audits.',
      th: 'ทุกรุ่นต้องผ่านกระบวนการตรวจคุณภาพอัตโนมัติ ทั้งการตรวจโค้ด ชนิดข้อมูล การทดสอบยูนิต ความครอบคลุมของคำแปล และการตรวจสอบ SEO การเข้าถึง และประสิทธิภาพ',
    },
    changes: [
      {
        kind: 'added',
        text: {
          en: 'Automated test suite covering security headers, redirects, i18n parity and money formatting',
          th: 'ชุดทดสอบอัตโนมัติครอบคลุมหัวข้อความความปลอดภัย การเปลี่ยนเส้นทาง ความเท่ากันของภาษา และรูปแบบจำนวนเงิน',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Automated SEO, accessibility and performance audits with pass/fail gates',
          th: 'การตรวจสอบ SEO การเข้าถึง และประสิทธิภาพอัตโนมัติพร้อมเกณฑ์ผ่าน/ไม่ผ่าน',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'CI pipeline that builds, boots the production server and runs every check against it',
          th: 'กระบวนการ CI ที่บิลด์ เปิดเซิร์ฟเวอร์จริง และรันการตรวจสอบทั้งหมดกับเซิร์ฟเวอร์นั้น',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Public engineering page documenting stack, security posture and operations',
          th: 'หน้างานวิศวกรรมสาธารณะที่บันทึกเทคโนโลยี สถานะความปลอดภัย และการดำเนินงาน',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Skip link, page landmarks, labelled form controls and AA-compliant colour contrast across the site',
          th: 'ลิงก์ข้ามไปยังเนื้อหา โครงสร้างหน้า แบบฟอร์มที่มีป้ายกำกับ และสีที่ผ่านมาตรฐาน AA ทั่วทั้งเว็บไซต์',
        },
      },
      {
        kind: 'fixed',
        text: {
          en: 'Bilingual coverage for interface copy that was silently missing Thai translations',
          th: 'ความครอบคลุมภาษาไทยสำหรับข้อความบนหน้าจอที่เคยขาดคำแปลเงียบ ๆ',
        },
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-09-16',
    title: {
      en: 'Bilingual Thai and English throughout the site',
      th: 'ใช้ได้ทั้งภาษาไทยและอังกฤษทั่วทั้งเว็บไซต์',
    },
    summary: {
      en: 'The entire public site now switches between Thai and English, with dates, numbers and currencies formatted per locale.',
      th: 'เว็บไซต์สาธารณะทั้งหมดสลับภาษาไทยและอังกฤษได้ โดยจัดรูปแบบวันที่ ตัวเลข และสกุลเงินตามภาษา',
    },
    changes: [
      {
        kind: 'added',
        text: {
          en: 'Thai and English language switch with live document language updates for screen readers',
          th: 'สวิตช์ภาษาไทยและอังกฤษ พร้อมอัปเดตภาษาเอกสารให้โปรแกรมอ่านหน้าจอได้ทันที',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Thai date rendering including Buddhist-era years',
          th: 'การแสดงวันที่แบบไทย รวมถึงปี พ.ศ.',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Currency display in USD and THB with a single conversion-rate source',
          th: 'การแสดงสกุลเงิน USD และ THB โดยใช้แหล่งอัตราแลกเปลี่ยนจุดเดียว',
        },
      },
      {
        kind: 'changed',
        text: {
          en: 'Interface copy moved to one dictionary so translations cannot drift per page',
          th: 'ย้ายข้อความบนหน้าจอไปรวมที่คำศัพท์เดียว เพื่อไม่ให้คำแปลคลาดกันไปในแต่ละหน้า',
        },
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-08-30',
    title: { en: 'Security hardening', th: 'เพิ่มความปลอดภัย' },
    summary: {
      en: 'Hardened the public surface with enforcing headers, strict redirect governance and stronger cookie consent.',
      th: 'เพิ่มความปลอดภัยให้พื้นที่สาธารณะด้วยหัวข้อความบังคับใช้ การกำกับดูเส้นทางเปลี่ยนหน้าอย่างเข้มงวด และการยินยอมคุกกี้ที่ชัดเจนขึ้น',
    },
    changes: [
      {
        kind: 'security',
        text: {
          en: 'Enforcing Content-Security-Policy, HSTS, frame denial, referrer and permissions policies',
          th: 'บังคับใช้ Content-Security-Policy, HSTS, การปฏิเสธการฝังเฟรม และนโยบาย referrer กับ permissions',
        },
      },
      {
        kind: 'security',
        text: {
          en: 'Canonical-host redirects: one host, one path per page, legacy campaign parameters dropped',
          th: 'เปลี่ยนเส้นทางไปยังโฮสต์หลัก มีหนึ่งโฮสต์ต่อหนึ่งหน้า และตัดพารามิเตอร์แคมเปญเก่าออก',
        },
      },
      {
        kind: 'security',
        text: {
          en: 'Rate limiting and validation on all public forms',
          th: 'จำกัดอัตราและตรวจสอบข้อมูลในทุกแบบฟอร์มสาธารณะ',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Cookie consent banner with per-category choices',
          th: 'แบนเนอร์ขอความยินยอมคุกกี้พร้อมตัวเลือกแยกตามประเภท',
        },
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-08-12',
    title: { en: 'Subscriptions and entitlements', th: 'แพ็กเกจและสิทธิ์การใช้งาน' },
    summary: {
      en: 'Plan-based feature and limit control, enforced server-side through the database.',
      th: 'ควบคุมฟีเจอร์และโควตาตามแพ็กเกจ โดยบังคับใช้ที่ฝั่งเซิร์ฟเวอร์ผ่านฐานข้อมูล',
    },
    changes: [
      {
        kind: 'added',
        text: {
          en: 'Plan catalogue with per-feature entitlements and resource limits',
          th: 'แคตตาล็อกแพ็กเกจพร้อมสิทธิ์แยกตามฟีเจอร์และโควตาการใช้ทรัพยากร',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Organisation isolation via row-level security across all tenant tables',
          th: 'แยกข้อมูลระหว่างองค์กรด้วย Row-Level Security ในทุกตารางของผู้ใช้',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Annual licence activation with server-side key validation',
          th: 'การเปิดใช้งานใบอนุญาตรายปี พร้อมตรวจสอบรหัสใบอนุญาตที่ฝั่งเซิร์ฟเวอร์',
        },
      },
      {
        kind: 'fixed',
        text: {
          en: 'Feature checks now resolve from entitlements instead of hard-coded plan names',
          th: 'การตรวจสิทธิ์ฟีเจอร์อ่านจากตารางสิทธิ์แทนชื่อแพ็กเกจที่เขียนตายตัว',
        },
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-07-24',
    title: { en: 'Documents and reporting', th: 'เอกสารและรายงาน' },
    summary: {
      en: 'Transactional document generation and the financial reporting suite.',
      th: 'สร้างเอกสารธุรกรรมและชุดรายงานการเงิน',
    },
    changes: [
      {
        kind: 'added',
        text: {
          en: 'Invoice, quotation, appraisal and memo PDF generation with company branding',
          th: 'สร้าง PDF ใบแจ้งหนี้ ใบเสนอราคา รายงานประเมิน และเมโม พร้อมตราสัญลักษณ์บริษัท',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Profit and loss, margins and supplier payables reporting',
          th: 'รายงานกำไรขาดทุน อัตรากำไร และยอดค้างชำระให้ซัพพลายเออร์',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Inventory aging and replenishment alerts',
          th: 'การแจ้งเตือนอายุสต็อกและจุดสั่งซื้อเติม',
        },
      },
      {
        kind: 'changed',
        text: {
          en: 'Reports respect organisation scope and row-level security',
          th: 'รายงานเคารพขอบเขตองค์กรและ Row-Level Security',
        },
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-07-03',
    title: { en: 'Customer portal and email', th: 'พอร์ทัลลูกค้าและอีเมล' },
    summary: {
      en: 'Customers gained self-service access; business email moved into the platform.',
      th: 'ลูกค้าใช้บริการตนเองได้ และย้ายอีเมลธุรกิจเข้ามาอยู่ในแพลตฟอร์ม',
    },
    changes: [
      {
        kind: 'added',
        text: {
          en: 'Customer portal for browsing available stones and tracking orders',
          th: 'พอร์ทัลลูกค้าสำหรับดูเพชรที่พร้อมจำหน่ายและติดตามคำสั่งซื้อ',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'In-app mailbox with per-user SMTP accounts, threads and CRM linking',
          th: 'กล่องจดหมายในระบบ พร้อมบัญชี SMTP รายผู้ใช้ เธรด และการเชื่อมกับ CRM',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Transactional registration and contact email through a dedicated edge function',
          th: 'อีเมลแจ้งเตือนการสมัครและติดต่อ ผ่าน edge function เฉพาะทาง',
        },
      },
      {
        kind: 'fixed',
        text: {
          en: 'Attachment handling for invoices and quotations in the mailbox',
          th: 'การจัดการไฟล์แนบของใบแจ้งหนี้และใบเสนอราคาในกล่องจดหมาย',
        },
      },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-06-18',
    title: { en: 'First public release', th: 'รุ่นแรกสู่สาธารณะ' },
    summary: {
      en: 'The diamond merchant platform goes live: inventory, CRM, sales, purchasing and memos in one system.',
      th: 'แพลตฟอร์มสำหรับผู้ค้าเพชรเปิดใช้งานจริง รวมสต็อก CRM การขาย การจัดซื้อ และเมโมไว้ในระบบเดียว',
    },
    changes: [
      {
        kind: 'added',
        text: {
          en: 'Diamond inventory with 4C attributes, QR labels and certificate linking',
          th: 'สต็อกเพชรพร้อมคุณสมบัติ 4C ฉลาก QR และการเชื่อมใบรับรอง',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'CRM for customers and suppliers with wishlists and ratings',
          th: 'CRM สำหรับลูกค้าและซัพพลายเออร์ พร้อมรายการโปรดและคะแนนประเมิน',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'Sales, purchases, memos and holds with full transaction history',
          th: 'การขาย การจัดซื้อ เมโม และการจอง พร้อมประวัติธุรกรรมทั้งหมด',
        },
      },
      {
        kind: 'added',
        text: {
          en: 'User management with role-based access control and audit logging',
          th: 'การจัดการผู้ใช้พร้อมการควบคุมสิทธิ์ตามบทบาทและบันทึกการตรวจสอบ',
        },
      },
    ],
  },
];

export const latestRelease = changelog[0];

/**
 * Page copy for /changelog. Lives beside the entries (not in the component)
 * so the bilingual unit test can enforce coverage on it too.
 */
export const changelogCopy: Record<
  'EN' | 'TH',
  { title: string; blurb: string; rss: string; breaking: string }
> = {
  EN: {
    title: 'Changelog',
    blurb:
      'Every release we ship, dated and described. Breaking changes are flagged and announced at least 30 days ahead in the product.',
    rss: 'Subscribe via RSS',
    breaking: 'Breaking',
  },
  TH: {
    title: 'บันทึกการเปลี่ยนแปลง',
    blurb:
      'ทุกรุ่นที่เราปล่อย พร้อมวันที่และรายละเอียด การเปลี่ยนที่พักภูมิจะถูกทำเครื่องหมายและแจ้งล่วงหน้าอย่างน้อย 30 วันในตัวผลิตภัณฑ์',
    rss: 'ติดตามผ่าน RSS',
    breaking: 'พักภูมิ',
  },
};
